// Shared HTTP plumbing: translates a non-2xx Response into the canonical
// ApiError shape (vouchers `parseErrorResponse` semantics). Wire format is
// RFC 9457 application/problem+json from kore's problem details, the same
// contract the Kotlin services answer with: `title` is the status's reason
// phrase, `detail` the message, `code` the stable key copy is chosen by.
// Members are parsed defensively: a 405 carries no `code`, and a proxy's
// error page is no problem at all.

import { ApiError, type FieldErrorSpec, UNKNOWN_CODE } from "./error";

interface ProblemBody {
    title?: string;
    code?: string;
    detail?: string;
    instance?: string;
    requestId?: string;
    errors?: FieldErrorSpec[];
    params?: Record<string, string | null>;
}

export async function parseErrorResponse(res: Response): Promise<ApiError> {
    const requestIdHeader = res.headers.get("X-Request-Id") ?? undefined;
    const contentType = res.headers.get("Content-Type") ?? "";
    const isStructured =
        contentType.startsWith("application/problem+json") || contentType.startsWith("application/json");

    // Read the body as text once. JSON parsing branches off this string so the
    // raw text remains available for the opaque fallback even if JSON.parse fails.
    let text = "";
    try {
        text = await res.text();
    } catch {
        // Stream error (rare). Leave text empty; the ApiError below carries status only.
    }

    if (isStructured && text.length > 0) {
        try {
            const body = JSON.parse(text) as ProblemBody;
            return new ApiError({
                status: res.status,
                code: body.code ?? UNKNOWN_CODE,
                title: body.title ?? undefined,
                detail: body.detail ?? undefined,
                instance: body.instance ?? undefined,
                errors: body.errors ?? undefined,
                params: body.params ?? undefined,
                // The body names the id the server logged; the header covers a
                // problem rendered before kore's middleware saw the request.
                requestId: body.requestId || requestIdHeader,
            });
        } catch {
            // Body wasn't valid JSON despite the content-type — fall through to opaque.
        }
    }

    // Opaque body (HTML 503, raw proxy error, broken JSON despite structured CT).
    // Capture up to 2000 chars for forensic value — opaque bodies are often
    // multi-line HTML or JSON fragments where the first 200 chars aren't enough
    // to identify the root cause.
    return new ApiError({
        status: res.status,
        code: UNKNOWN_CODE,
        detail: text.slice(0, 2000) || undefined,
        requestId: requestIdHeader,
    });
}
